<template>
  <AppWithLoading :fetch="fetchAll" v-slot:default="{loader, localLoading}">
    <div class="referral-program">
      <template v-if="!localLoading">
        <div class="referral-program__title">
          <div>{{ $t('titleText1') }}<span>{{ company.referralPayoutPercent }}%</span>{{ $t('titleText2') }}</div>
          <div>{{ $t('titleText3') }}<span>{{ company.referralDiscountPercent }}%</span>{{ $t('titleText4') }}</div>
        </div>

        <div class="referral-program__link">
          <span>{{ referralLink }}</span>
          <button @click="copyReferralLink">
            <template v-if="!linkCopied">
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
              >
                <path d="M11.25 15H3.75C2.7558 14.9988 1.80267 14.6033 1.09966 13.9003C0.396661 13.1973 0.00119089 12.2442 0 11.25L0 3.75C0.00119089 2.7558 0.396661 1.80267 1.09966 1.09966C1.80267 0.396661 2.7558 0.00119089 3.75 0L11.25 0C12.2442 0.00119089 13.1973 0.396661 13.9003 1.09966C14.6033 1.80267 14.9988 2.7558 15 3.75V11.25C14.9988 12.2442 14.6033 13.1973 13.9003 13.9003C13.1973 14.6033 12.2442 14.9988 11.25 15ZM18 14.25V4.5C18 4.30109 17.921 4.11032 17.7803 3.96967C17.6397 3.82902 17.4489 3.75 17.25 3.75C17.0511 3.75 16.8603 3.82902 16.7197 3.96967C16.579 4.11032 16.5 4.30109 16.5 4.5V14.25C16.5 14.8467 16.2629 15.419 15.841 15.841C15.419 16.2629 14.8467 16.5 14.25 16.5H4.5C4.30109 16.5 4.11032 16.579 3.96967 16.7197C3.82902 16.8603 3.75 17.0511 3.75 17.25C3.75 17.4489 3.82902 17.6397 3.96967 17.7803C4.11032 17.921 4.30109 18 4.5 18H14.25C15.2442 17.9988 16.1973 17.6033 16.9003 16.9003C17.6033 16.1973 17.9988 15.2442 18 14.25Z" fill="#757575" />
              </svg>
            </template>

            <template v-else>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                x="0px"
                y="0px"
                width="100"
                height="100"
                viewBox="0 0 30 30"
              >
                <path d="M 26.980469 5.9902344 A 1.0001 1.0001 0 0 0 26.292969 6.2929688 L 11 21.585938 L 4.7070312 15.292969 A 1.0001 1.0001 0 1 0 3.2929688 16.707031 L 10.292969 23.707031 A 1.0001 1.0001 0 0 0 11.707031 23.707031 L 27.707031 7.7070312 A 1.0001 1.0001 0 0 0 26.980469 5.9902344 z" />
              </svg>
            </template>
          </button>
        </div>
        <div class="referral-program__link_label">
          {{ $t('copyLinkAnd') }}
        </div>

        <div class="referral-program__hr" />

        <div class="referral-program__stats">
          <h2 class="referral-program__stats_title">
            {{ $t('stats') }}
          </h2>

          <ul class="referral-program__stats_blocks">
            <li class="referral-program__stats_block">
              <span>{{ referralInfo[0].companiesCount }}</span>
              <div>{{ $t('registerLink') }}</div>
            </li>
            <li class="referral-program__stats_block">
              <span>{{ referralInfo[0].companiesWithPaymentsCount }}</span>
              <div>{{ $t('paidTheTariff') }}</div>
            </li>
            <li class="referral-program__stats_block">
              <span>{{ referralInfoMoney }}</span>
              <div>{{ $t('yourReward') }}</div>
            </li>
          </ul>
        </div>
      </template>
    </div>
  </AppWithLoading>
</template>

<script>
import AppWithLoading from '@/components/AppWithLoading';
import { ref, computed, onMounted } from '@vue/composition-api';
import copy from 'clipboard-copy';
import useReferral from '@/compositions/useReferral.js';
import useCompany from '@/compositions/useCompany';
import useNumberFormatter from '@/compositions/useNumberFormatter';

export default {
  name: 'Referral',
  components: {
    AppWithLoading,
  },
  setup() {
    const linkCopied = ref(false);
    const { fetchReferralInfo, referralInfo } = useReferral();
    const { company, fetch } = useCompany();
    const { formatCurrencyFromMoney } = useNumberFormatter();
    const frontendUrl = ref(process.env.VUE_APP_FRONT_URL);

    const referralLink = computed(() => `${frontendUrl.value}?referralCode=${company.value.id}`);

    const referralInfoMoney = computed(() => (!referralInfo.value[0].moneyInfo.length ? 0
      : formatCurrencyFromMoney(
        {
          value: referralInfo.value[0].moneyInfo[0].payout_sum,
          currency: referralInfo.value[0].moneyInfo[0].currency,
        },
      )));

    function copyReferralLink() {
      copy(referralLink.value);
      linkCopied.value = true;
    }

    async function fetchAll() {
      await fetch();
      await fetchReferralInfo();
    }

    onMounted(async () => {
      await fetchReferralInfo();
    });

    return {
      fetchAll,
      referralInfo,
      linkCopied,
      copyReferralLink,
      referralLink,
      company,
      referralInfoMoney,
    };
  },
};
</script>

<style lang="scss" scoped>
.referral-program {
  min-height: calc(100vh - 12rem);
  padding: 2.1875rem 2.5rem 2.5rem 2.5rem;
  &__title {
    color: #212121;
    font-size: 19px;
    line-height: 19px;
    font-weight: 400;
    margin-bottom: 25px;
    div {
      margin-bottom: 5px;
    }
    span {
      color: #0A82D9;
    }
  }
  &__hr {
    height: 1px;
    width: 100%;
    background-color: #DDDDDD;
    margin-bottom: 30px;
  }
  &__link {
    border: 1px solid #C2C2C2;
    padding: 12px;
    max-width: fit-content;
    border-radius: 4px;
    margin-bottom: 10px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    span {
        font-size: 18px;
        color: #757575;
        font-weight: 400;
        line-height: 20px;
        @media screen and (max-width: 599px) {
          height: 20px;
          overflow: hidden;
          font-size: 16px;
        }
    }
    button {
        margin-top: 3px;
        margin-left: 115px;
        @media screen and (max-width: 599px) {
          margin-left: 15px;
        }
        svg {
            width: 18px;
            height: 18px;
        }
    }
    &_label {
      font-size: 14px;
      color: #757575;
      font-weight: 300;
      line-height: 16.8px;
      margin-bottom: 30px;
      @media screen and (max-width: 599px) {
        font-size: 12px;
      }
    }
  }
  &__stats {
    &_title {
      color: #212121;
      font-size: 19px;
      line-height: 19px;
      font-weight: 400;
      margin-bottom: 30px;
      @media screen and (max-width: 599px) {
        font-size: 18px;
        line-height: 18px;
      }
    }
    &_blocks {
      display: flex;
      align-items: center;
      padding: 0;
      margin: 0;
      li:nth-child(3) {
          margin-right: 0;
      }
      @media screen and (max-width: 599px) {
        flex-direction: column;
        li:nth-child(3) {
          margin-bottom: 0;
        }
      }
    }
    &_block {
      display: flex;
      flex-direction: column;
      align-items: center;
      list-style-type: none;
      background-color: #F5F5F5;
      width: 33%;
      padding: 40px 0;
      border-radius: 10px;
      text-align: center;
      height: 157px;
      font-size: 16px;
      line-height: 22px;
      margin-right: 30px;
      @media screen and (max-width: 768px) {
        font-size: 13px;
        margin-right: 15px;
      }
      @media screen and (max-width: 599px) {
        width: 100%;
        margin-right: 0;
        margin-bottom: 10px;
      }
      span {
        font-size: 40px;
        line-height: 40px;
        margin-bottom: 15px;
        @media screen and (max-width: 860px) {
          font-size: 30px;
        }
      }
    }
  }
}

@media #{map-get($display-breakpoints, 'md-and-down')} {
    .referral-program {
      padding: 1.875rem 1rem 0.125rem;
      &__title {
        font-size: 1.25rem;
        margin-bottom: 1rem;
      }
    }
  }
  @media #{map-get($display-breakpoints, 'xs-only')} {
    .referral-program {
      padding: 1.25rem 0.625rem 0.125rem;
      &__title {
        font-size: 1.15rem;
        line-height: 1.2;
        font-weight: 500;
        margin-bottom: 0.75rem;
      }
    }
  }
</style>

<i18n lang="yaml" locale="ru">
titleText1: 'Поделитесь ссылкой на Huntica и получите вознаграждение '
titleText2: ' за каждый оплаченный тариф.'
titleText3: 'Те, кто воспользуются вашей реферальной ссылкой, получат скидку в '
titleText4: ' на первую оплату любого тарифного плана.'
generateReferralLink: Сгенерировать реферальную ссылку
yourReferralLink: Ваша реферальная ссылка
copy: Скопировать
linkCopied: Ссылка скопирована!
stats: Статистика за все время
registerLink: Зарегистрировались по ссылке
paidTheTariff: Оплатили тариф
yourReward: Ваше вознаграждение
copyLinkAnd: Скопируй и поделись ссылкой на Huntica и получай прибыль ежемесячно.
</i18n>

<i18n lang="yaml" locale="en">
titleText1: 'Share the Huntica referral link and get rewarded '
titleText2: ' for each paid tariff.'
titleText3: 'Anyone who uses your referral link will also receive a '
titleText4: ' discount on their first payment for any tariff plan.'
generateReferralLink: Generate referral link
yourReferralLink: Your referral link
copy: Copy
linkCopied: Link copied!
stats: All-time statistics
registerLink: Registered using the link
paidTheTariff: Paid the tariff
yourReward: Your reward
copyLinkAnd: Copy and share the Huntica referral link and earn monthly profits.
</i18n>
